// Mapeo centralizado de `reason` codes do backend a mensaxes amables
// para usuario. Compartido por apps/web e mobile.
//
// Patrón: o backend devolve respostas tipo
//   { error: "BadRequest", reason: "buyer_kyc_not_found" }
// que o frontend NUNCA debe pintar literais ao usuario. En lugar diso,
// pasa a `describeBackendReason(reason)` que devolve unha string lista
// para mostrar.
//
// As mensaxes están en galego (idioma principal v1). O frontend pode:
//   - usar directo o resultado (gl)
//   - mapear a unha i18n key se precisa multi-idioma (`'error.reason.<code>'`)
//
// Cando engadas un código novo aquí, considera engadir tamén a clave
// en `apps/web/src/i18n/locales/{gl,es}.ts` e `mobile/src/i18n/*.json`
// baixo `error.reason.<code>` para ter es/pt cando faga falta.
/**
 * Mensaxes amables (gl) para os ~50 reason codes que devolve o backend.
 * Cando o backend devolve un código non incluído aquí, o mapper devolve
 * un fallback xenérico ou (en debug) o código bruto.
 */
export const BACKEND_REASON_COPY = {
    // ───────────── Auth / usuario ─────────────
    user_not_found: 'Non encontramos a túa conta.',
    user_vanished: 'A túa sesión expirou. Volve iniciar sesión.',
    email_required: 'O correo é obrigatorio.',
    email_already_used: 'Xa existe unha conta con ese correo.',
    email_not_verified: 'Tes que verificar o teu correo antes de continuar.',
    invalid_credentials: 'Credenciais incorrectas. Revisa o correo e contrasinal.',
    password_too_short: 'O contrasinal debe ter polo menos 8 caracteres.',
    password_required: 'O contrasinal é obrigatorio.',
    totp_required: 'Introduce o código da app autenticadora.',
    totp_invalid: 'Código de autenticación incorrecto.',
    rate_limited: 'Demasiadas tentativas. Espera uns minutos e tenta de novo.',
    registration_closed: 'O rexistro de novas contas está pechado neste momento. Se xa tes conta, inicia sesión.',
    private_beta_mode: 'A plataforma está en modo beta privado. Só usuarios convidados poden rexistrarse.',
    token_expired: 'Este código xa caducou. Solicita un novo.',
    token_invalid: 'O código non é válido. Comproba que o copiaches enteiro.',
    session_expired: 'A túa sesión expirou. Volve iniciar sesión.',
    // ───────────── Roles ─────────────
    role_not_held: 'Non tes este rol activo. Engádeo dende Perfil > Roles.',
    role_must_be_SILLEDA_ADMIN: 'Esta acción só está dispoñible para administración.',
    FARMER_role_required: 'Esta acción require o rol de gandeiro.',
    VET_role_required: 'Esta acción require o rol de veterinario.',
    BUYER_role_required: 'Esta acción require o rol de comprador.',
    TRANSPORTISTA_role_required: 'Esta acción require o rol de transportista.',
    // ───────────── KYC ─────────────
    kyc_required: 'Tes que verificar a túa identidade (KYC) antes de continuar.',
    kyc_pending: 'A túa verificación KYC está en revisión.',
    kyc_rejected: 'A túa verificación KYC foi rexeitada. Tenta de novo dende Perfil.',
    buyer_kyc_not_found: 'O comprador aínda non completou a verificación de identidade.',
    seller_kyc_not_found: 'O vendedor aínda non completou a verificación de identidade.',
    // ───────────── Animais / explotación ─────────────
    animal_not_found: 'Este animal non existe ou foi eliminado.',
    animal_not_owned: 'Este animal non pertence á túa explotación.',
    animal_sin_leyrospot: 'Este animal aínda non ten LeyroSpot asociado. Asóciao antes de continuar.',
    not_the_animal_owner: 'Non es o propietario actual deste animal.',
    not_the_farm_owner: 'Non es o propietario desta explotación.',
    farm_not_found: 'Non encontramos a túa explotación. Configúraa primeiro dende Perfil.',
    invalid_crotal: 'O crotal non é válido. Comproba o formato (ES + 13 díxitos).',
    duplicate_crotal: 'Xa existe un animal con ese crotal na túa explotación.',
    invalid_rega_code: 'O código REGA non é válido. Comproba o formato.',
    // ───────────── Marketplace ─────────────
    sale_not_found: 'Esta venda xa non está dispoñible.',
    sale_unavailable: 'Esta venda xa non está dispoñible.',
    sale_already_claimed: 'Esta venda xa foi reservada por outro comprador.',
    sale_already_delivered: 'Esta venda xa foi entregada.',
    cannot_buy_own_sale: 'Non podes mercar un animal da túa propia explotación.',
    stripe_connect_required: 'Debes vincular a túa conta bancaria con Stripe antes de poder vender. Vai a Perfil > Cobros.',
    billing_not_configured: 'O cobro automático non está configurado. Vai a Perfil > Cobros para activalo.',
    insufficient_balance: 'O comprador non ten saldo suficiente. Pídelle que revise o seu pago.',
    payment_failed: 'O pago non se puido completar. Tenta de novo nuns minutos.',
    contract_pending: 'O contrato segue pendente de sinatura.',
    signature_verification_failed: 'Non se puido verificar a sinatura electrónica. Tenta de novo.',
    // ───────────── EUDR ─────────────
    eudr_dds_not_generated: 'A Declaración de Dilixencia Debida (EUDR) aínda non se xerou para esta venda.',
    eudr_dds_required: 'É obrigatorio xerar a Declaración EUDR antes de continuar.',
    eudr_polygon_required: 'É obrigatorio definir o polígono GPS da explotación.',
    // ───────────── Transport ─────────────
    transport_not_found: 'Este transporte xa non está dispoñible.',
    transport_already_picked_up: 'Este transporte xa foi recollido.',
    transport_already_delivered: 'Este transporte xa foi entregado.',
    // ───────────── Documentos ─────────────
    pdf_not_found: 'Este documento aínda non está dispoñible. Tenta nuns minutos.',
    documenso_unavailable: 'O servizo de sinatura electrónica non está dispoñible agora mesmo.',
    // ───────────── Internos / xenéricos ─────────────
    validation_failed: 'Algún campo non é válido. Revísao e tenta de novo.',
    internal_error: 'Erro interno do servidor. Téntao nuns minutos.',
    unauthorized: 'A túa sesión expirou. Volve iniciar sesión.',
    forbidden: 'Non tes permiso para esta acción.',
    not_found: 'O que estás a buscar non existe.',
    network_error: 'Sen conexión. Comproba a túa rede e tenta de novo.',
};
const GENERIC_FALLBACK = 'Algo non foi como agardabamos. Tenta de novo nuns minutos.';
/**
 * Devolve unha mensaxe amable para o usuario a partir dun `reason` code
 * do backend. Se o código non está mapeado, devolve `fallback` (ou unha
 * mensaxe xenérica). Nunca devolve o código snake_case bruto.
 */
export function describeBackendReason(reason, fallback) {
    if (reason && BACKEND_REASON_COPY[reason]) {
        return BACKEND_REASON_COPY[reason];
    }
    // Algúns códigos veñen como "VET_role_required" coa convención CASE-mixed.
    if (reason) {
        const normalized = reason.toLowerCase();
        const match = Object.keys(BACKEND_REASON_COPY).find((k) => k.toLowerCase() === normalized);
        if (match)
            return BACKEND_REASON_COPY[match];
    }
    return fallback ?? GENERIC_FALLBACK;
}
export function describeBackendErrorPayload(payload, fallback) {
    if (!payload)
        return fallback ?? GENERIC_FALLBACK;
    if (payload.reason && BACKEND_REASON_COPY[payload.reason]) {
        return BACKEND_REASON_COPY[payload.reason];
    }
    // Se non hai reason mapeado, intenta error → message → fallback.
    return describeBackendReason(payload.reason, payload.error ?? payload.message ?? fallback);
}
//# sourceMappingURL=backend-errors.js.map